/**
 * Doctor profile page.
 *
 * Renders individual doctor profiles, including bio, qualifications, services,
 * and related cases. Credential fields respect attribution rules: demo credentials
 * are marked as "to be supplied by clinic" rather than rendered as fact.
 *
 * IMPORTANT: No Person/Physician schema is emitted for DEMO doctors.
 * isSchemaEligible(provenance) is false for all current records.
 */

import { useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { isSchemaEligible, isTrustworthy } from "@/content/types";
import { getDoctor, DOCTOR_SLUGS } from "@/content/doctors";
import { getService } from "@/content/services";
import { getCase } from "@/content/cases";
import { clinicNode } from "@/lib/schema";
import { localizedPath } from "@/lib/locale";
import { motion } from "framer-motion";
import { AlertCircle, BookOpen, Award, Users } from "lucide-react";
import BookingCTA from "@/components/BookingCTA";

interface DoctorPageProps {
  locale: Locale;
}

export default function DoctorPage({ locale }: DoctorPageProps) {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useLanguage();

  if (!slug || !DOCTOR_SLUGS.includes(slug)) {
    return (
      <>
        <Seo
          title={locale === "ar" ? "غير موجود" : "Not Found"}
          description={locale === "ar" ? "الصفحة غير موجودة" : "Page not found"}
          path={`/${locale}/doctors/${slug}`}
          locale={locale}
          noindex
        />
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-3xl font-bold text-white">
            {locale === "ar" ? "غير موجود" : "Not Found"}
          </h1>
        </main>
      </>
    );
  }

  const doctor = getDoctor(slug)!;

  const title =
    locale === "ar"
      ? `${doctor.name[locale]} - طبيب أسنان | عيادة بريميرو`
      : `${doctor.name[locale]} - Dentist | Primero Dental Clinic`;

  const description =
    locale === "ar"
      ? `تخصص: ${doctor.specialty[locale]}`
      : `Specialty: ${doctor.specialty[locale]}`;

  const demoNoticeTitle =
    locale === "ar" ? "بيانات تجريبية" : "Demo Profile";
  const demoNoticeText =
    locale === "ar"
      ? "هذا الملف بيانات تجريبية مؤقتة للمراجعة. الملف الحقيقي المعتمد من العيادة سيحل محله قريبًا."
      : "This is a temporary demo profile for review. A verified clinic-approved profile will be coming soon.";

  // Linked services that actually exist
  const linkedServices = doctor.services.filter((sSlug) => getService(sSlug));

  // Linked cases
  const linkedCases = doctor.cases
    .map((cSlug) => getCase(cSlug))
    .filter((c) => c !== undefined) as typeof getCase extends (s: string) => infer T | undefined
    ? Exclude<T, undefined>[]
    : never;

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/${locale}/doctors/${slug}`}
        locale={locale}
        schema={clinicNode(locale)}
      />
      <main className="container mx-auto px-4 py-12">
        {/* Demo Notice */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-8 p-4 rounded-lg border border-amber-600/50 bg-amber-900/20 flex gap-3 items-start"
        >
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-semibold text-amber-300 mb-1">{demoNoticeTitle}</h2>
            <p className="text-sm text-amber-200/90">{demoNoticeText}</p>
          </div>
        </motion.div>

        {/* Header with Photo and Basic Info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="grid md:grid-cols-3 gap-8 mb-12"
        >
          {/* Photo */}
          <div className="md:col-span-1">
            <div className="aspect-square rounded-xl overflow-hidden bg-slate-800 border border-slate-700">
              <img
                src={doctor.photo.src}
                alt={doctor.photo.alt[locale]}
                width={doctor.photo.width}
                height={doctor.photo.height}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Basic Info */}
          <div className="md:col-span-2">
            <h1 className="text-4xl font-bold text-white mb-2">
              {doctor.name[locale]}
            </h1>
            <p className="text-xl text-primary font-semibold mb-6">
              {doctor.specialty[locale]}
            </p>

            {/* Languages */}
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                {locale === "ar" ? "اللغات" : "Languages"}
              </h2>
              <ul className="space-y-1">
                {doctor.languages[locale].map((lang, i) => (
                  <li key={i} className="text-slate-400">
                    • {lang}
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <BookingCTA
              trigger={
                <button className="px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-colors">
                  {locale === "ar"
                    ? "حجز موعد استشارة"
                    : "Book Consultation"}
                </button>
              }
            />
          </div>
        </motion.div>

        {/* Bio */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-4">
            {locale === "ar" ? "نبذة مهنية" : "Professional Background"}
          </h2>
          <div className="space-y-4">
            {doctor.bio[locale].map((para, i) => (
              <p key={i} className="text-slate-300 leading-relaxed">
                {para}
              </p>
            ))}
          </div>
        </motion.section>

        {/* Credentials Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12 space-y-6"
        >
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            {locale === "ar" ? "المؤهلات والشهادات" : "Qualifications"}
          </h2>

          {/* Education */}
          <div>
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              {locale === "ar" ? "التعليم" : "Education"}
            </h3>
            {isTrustworthy(doctor.education.provenance) ? (
              <ul className="space-y-2">
                {doctor.education.value[locale].map((item, i) => (
                  <li key={i} className="text-slate-400">
                    • {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-amber-300 italic">
                {doctor.education.replaceWith}
              </p>
            )}
          </div>

          {/* Certifications */}
          <div>
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              {locale === "ar" ? "الشهادات المهنية" : "Certifications"}
            </h3>
            {isTrustworthy(doctor.certifications.provenance) ? (
              <ul className="space-y-2">
                {doctor.certifications.value[locale].map((item, i) => (
                  <li key={i} className="text-slate-400">
                    • {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-amber-300 italic">
                {doctor.certifications.replaceWith}
              </p>
            )}
          </div>

          {/* Memberships */}
          <div>
            <h3 className="text-lg font-semibold text-slate-200 mb-3">
              {locale === "ar" ? "الانتسابات" : "Memberships"}
            </h3>
            {isTrustworthy(doctor.memberships.provenance) ? (
              <ul className="space-y-2">
                {doctor.memberships.value[locale].map((item, i) => (
                  <li key={i} className="text-slate-400">
                    • {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-amber-300 italic">
                {doctor.memberships.replaceWith}
              </p>
            )}
          </div>

          {/* Years of Experience */}
          {doctor.yearsExperience.value > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-200 mb-3">
                {locale === "ar" ? "سنوات الخبرة" : "Years of Experience"}
              </h3>
              {isTrustworthy(doctor.yearsExperience.provenance) ? (
                <p className="text-slate-400">{doctor.yearsExperience.value} years</p>
              ) : (
                <p className="text-amber-300 italic">
                  {doctor.yearsExperience.replaceWith}
                </p>
              )}
            </div>
          )}
        </motion.section>

        {/* Clinical Interests */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            {locale === "ar" ? "الاهتمامات السريرية" : "Clinical Interests"}
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {doctor.clinicalInterests[locale].map((interest, i) => (
              <div
                key={i}
                className="p-3 rounded-lg bg-slate-900/50 border border-slate-700 text-slate-300"
              >
                {interest}
              </div>
            ))}
          </div>
        </motion.section>

        {/* Services */}
        {linkedServices.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h3 className="text-lg font-semibold text-slate-200 mb-4">
              {locale === "ar" ? "الخدمات" : "Services"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {linkedServices.map((sSlug) => {
                const service = getService(sSlug)!;
                return (
                  <a
                    key={sSlug}
                    href={localizedPath(locale, `/services/${sSlug}`)}
                    className="p-4 rounded-lg border border-slate-700 bg-slate-900/50 hover:border-primary/50 hover:bg-slate-900/80 transition-all"
                  >
                    <h4 className="font-semibold text-white mb-2">
                      {service.name[locale]}
                    </h4>
                    <p className="text-sm text-slate-400 line-clamp-2">
                      {service.summary[locale]}
                    </p>
                  </a>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* Related Cases */}
        {linkedCases.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h3 className="text-lg font-semibold text-slate-200 mb-4">
              {locale === "ar" ? "الحالات السريرية" : "Clinical Cases"}
            </h3>
            <div className="space-y-3">
              {linkedCases.map((c) => (
                <a
                  key={c.slug}
                  href={localizedPath(locale, `/cases/${c.slug}`)}
                  className="block p-4 rounded-lg border border-slate-700 bg-slate-900/50 hover:border-primary/50 hover:bg-slate-900/80 transition-all"
                >
                  <h4 className="font-semibold text-white mb-1">
                    {c.title[locale]}
                  </h4>
                  <p className="text-sm text-slate-400">
                    {getService(c.service)?.name[locale]}
                  </p>
                </a>
              ))}
            </div>
          </motion.section>
        )}
      </main>
    </>
  );
}
